import React, { ComponentType, ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { LEAD_FINDER_PLAN_ORDER } from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import {
  getFilteredLeadFinderRecords,
  isAddRecordsVisible,
  isResultModalVisible,
} from "@app/component/leadFinder/store/selectors";
import { EntityItemProperties } from "@app/component/RecordCard";
import { RecordList, TextGetters } from "@app/component/RecordList";
import { getLeadGenPlan } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { leadIdGetter } from "@app/util/map/idGetters";

import BasicPlanBanner from "../BasicPlanBanner";
import ResultsModal from "../ResultsModal";

import messages from "./messages";
import styles from "./ResultsPane.module.scss";

// No server-side pagination for lead finder results and no range prop is passed,
// so pagination never renders; the value only satisfies the required prop.
const PAGE_SIZE = 50;

export interface ResultsPaneProps {
  actionLabel: ReactNode;
  extraToolbar?: ReactNode;
  itemComponent: ComponentType<EntityItemProperties<Lead>>;
  onActionClick?: () => void;
  onClearSelection: () => void;
  onSelectAll: (placeIds: Array<Lead["place_id"]>) => void;
  selectedCount: number;
  selectedIds: Set<Lead["place_id"]>;
  wizard?: ReactNode;
}

const ResultsPane = ({
  actionLabel,
  addRecordsVisible,
  extraToolbar,
  isNotOnHighestPlan,
  itemComponent,
  onActionClick,
  onClearSelection,
  onSelectAll,
  records,
  resultsModalVisible,
  selectedCount,
  selectedIds,
  wizard,
}: ResultsPaneProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const [search, setSearch] = useState("");
  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? records.filter((lead) => lead.name.toLowerCase().includes(query)) : records;
  }, [records, search]);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: (count: number) => intl.formatMessage(messages.count, { count }),
    }),
    [intl]
  );

  const allSelected =
    filteredLeads.length > 0 && filteredLeads.every((lead) => selectedIds.has(leadIdGetter(lead)));
  const allVisible = filteredLeads.length === records.length;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onClearSelection();
    } else {
      onSelectAll(filteredLeads.map(leadIdGetter));
    }
  }, [allSelected, filteredLeads, onClearSelection, onSelectAll]);

  const hasSelection = selectedCount > 0;

  return (
    <>
      <div className={styles.selectionBar}>
        <div className={styles.selection}>
          <div className={styles.selectionCount}>
            {hasSelection
              ? intl.formatMessage(messages.selected, {
                  selectedRecordsCount: selectedCount,
                  total: records.length,
                })
              : intl.formatMessage(messages.emptySelection)}
          </div>
          <Button disabled={!onActionClick || !hasSelection} onClick={onActionClick} type="primary">
            {actionLabel}
          </Button>
          {hasSelection && addRecordsVisible && wizard}
          {resultsModalVisible && <ResultsModal />}
        </div>
      </div>
      {extraToolbar && <div className={styles.toolbar}>{extraToolbar}</div>}
      <RecordList<Lead>
        banner={isNotOnHighestPlan ? <BasicPlanBanner /> : undefined}
        headerClassName={styles.header}
        idGetter={leadIdGetter}
        itemComponent={itemComponent}
        onSearch={setSearch}
        pageSize={PAGE_SIZE}
        records={filteredLeads}
        searchActions={
          filteredLeads.length ? (
            <Button onClick={handleSelectAll} type="link">
              {allSelected
                ? intl.formatMessage(messages.clearSelected)
                : intl.formatMessage(messages.selectAllLeads, { allVisible })}
            </Button>
          ) : undefined
        }
        searchPlaceholder={intl.formatMessage(messages.searchPlaceholder)}
        showCount
        textGetters={textGetters}
        total={filteredLeads.length}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  addRecordsVisible: isAddRecordsVisible(state),
  isNotOnHighestPlan: getLeadGenPlan(state) !== LEAD_FINDER_PLAN_ORDER.at(-1),
  records: getFilteredLeadFinderRecords(state),
  resultsModalVisible: isResultModalVisible(state),
});

export default connect(mapStateToProps)(ResultsPane);
